import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CertificateTemplateEditor,
  initialPlaceholders,
  Input,
} from '@mlm/ui';
import type { CertificatePlaceholder } from '@mlm/shared';
import {
  certificateTemplateImageUrl,
  createCertificateTemplate,
  deleteCertificateTemplate,
  listCertificateTemplates,
  updateCertificateTemplate,
} from '../lib/creators-api';
import { useHasPerm } from '../lib/auth-store';

/**
 * House certificate templates — the designs the tenant's OWN courses issue.
 *
 * Until now these existed only as API routes: house courses could be authored
 * but never given a certificate, because nothing in this SPA could reach the
 * endpoints and the creator portal only lists a creator's own templates.
 *
 * ASSIGNING a template to a course is deliberately not here. House courses are
 * authored through the house creator account (`creators.is_house`), and that
 * portal's course editor already offers every house template, marked
 * "(platform)". One authoring surface, not two.
 */
export function CertificateTemplatesPage() {
  const qc = useQueryClient();
  const canWrite = useHasPerm('product.write');
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['admin-certificate-templates'],
    queryFn: listCertificateTemplates,
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = templates.find((t) => t.id === selectedId) ?? null;
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);

  const create = useMutation({
    meta: { successMessage: 'Template created — now place the fields.' },
    mutationFn: () =>
      createCertificateTemplate(newName, newFile!, { placeholders: initialPlaceholders() }),
    onSuccess: (row) => {
      setNewName('');
      setNewFile(null);
      qc.invalidateQueries({ queryKey: ['admin-certificate-templates'] });
      setSelectedId(row.id);
    },
  });
  const save = useMutation({
    meta: { successMessage: 'Template saved.' },
    mutationFn: (v: {
      id: string;
      name: string;
      config: { placeholders: CertificatePlaceholder[] };
      imageFile: File | null;
    }) => updateCertificateTemplate(v.id, { name: v.name, config: v.config }, v.imageFile),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-certificate-templates'] }),
  });
  const remove = useMutation({
    meta: { successMessage: 'Template deleted.' },
    mutationFn: (id: string) => deleteCertificateTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-certificate-templates'] });
      setSelectedId(null);
    },
  });

  return (
    <div className="max-w-5xl space-y-4">
      <h1 className="text-2xl font-semibold">Certificate templates</h1>
      <p className="text-sm text-muted-foreground">
        Designs your own courses issue on completion. Upload a background (PNG/JPEG, landscape works
        best) and place the learner&rsquo;s name on it. Assign one to a course from the course
        editor, where these appear as &ldquo;(platform)&rdquo; templates.
      </p>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-3">
          {canWrite && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">New template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Name"
                />
                <Input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setNewFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  size="sm"
                  className="w-full"
                  disabled={!newName || !newFile || create.isPending}
                  onClick={() => create.mutate()}
                >
                  Upload
                </Button>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <p className="p-4 text-sm text-muted-foreground">Loading…</p>
              ) : templates.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No templates yet.</p>
              ) : (
                <ul>
                  {templates.map((t) => (
                    <li key={t.id}>
                      <button
                        onClick={() => setSelectedId(t.id)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-muted/40 ${t.id === selectedId ? 'bg-accent font-medium' : ''}`}
                      >
                        {t.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
        <div>
          {selected ? (
            <CertificateTemplateEditor
              key={selected.id}
              templateId={selected.id}
              name={selected.name}
              // `?v=<imageKey>` busts the 5-minute asset cache the moment a
              // background is replaced; the key changes on every upload.
              imageUrl={`${certificateTemplateImageUrl(selected.id)}?v=${encodeURIComponent(selected.imageKey)}`}
              placeholders={selected.config.placeholders}
              saving={save.isPending}
              deleting={remove.isPending}
              allowImageReplace={canWrite}
              deleteDescription="Your courses using it will stop issuing certificates. Already-issued certificates are unaffected."
              onSave={(next) => save.mutate({ id: selected.id, ...next })}
              onDelete={() => remove.mutate(selected.id)}
            />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                Select a template to edit its layout.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
